import { ProductModel } from "@insite/client-framework/Types/ApiModels";

/**
 * Frontend helpers for MGBW fabric-swatch products on the swatches PLP.
 *
 * Swatch products are detected by the presence of the dedicated swatch size
 * variant trait ("MGBWFabricSwatchVariantTrait") on the product, which mirrors
 * the backend swatch StyleClass "MGBWFabricSwatchVariantType" (the product's
 * VariantTypeId). This matches the existing detection used in GVProductColors.
 */

/** Variant trait name that identifies MGBW fabric-swatch products (the size trait). */
export const SWATCH_VARIANT_TRAIT_NAME = "MGBWFabricSwatchVariantTrait";

/** AttributeType name whose value is shown as the swatch card's primary heading. */
export const SWATCH_COLOR_ATTRIBUTE_NAME = "uxColorName";

/** AttributeType name whose value is shown as the swatch card's secondary heading. */
export const SWATCH_COLLECTION_ATTRIBUTE_NAME = "uxCollectionName";

/**
 * Page-variant name (CMS) of the dedicated swatches PLP. The swatch-specific card
 * treatment (color/collection headings + Add to Cart) is limited to this variant;
 * on shared pages like search, swatch products render as regular product cards.
 */
export const SWATCHES_PAGE_VARIANT_NAME = "Swatches";

type ProductLike = Pick<ProductModel, "variantTraits" | "attributeTypes" | "properties"> | undefined | null;

/** True when the product is an MGBW fabric-swatch product. */
export function isSwatchProduct(product: ProductLike): boolean {
    return (
        product?.properties?.mgbw_isSwatch === "true" ||
        !!product?.variantTraits?.some(trait => trait.name === SWATCH_VARIANT_TRAIT_NAME)
    );
}

/**
 * Returns the joined display value(s) of a product AttributeType by its
 * (case-insensitive) name, or undefined when the product has no value for it.
 */
export function getProductAttributeValue(product: ProductLike, attributeTypeName: string): string | undefined {
    const attributeType = product?.attributeTypes?.find(type => type.name?.toLowerCase() === attributeTypeName.toLowerCase());
    const value = attributeType?.attributeValues
        ?.map(attributeValue => attributeValue.valueDisplay)
        .filter(Boolean)
        .join(", ");
    return value || undefined;
}
